import { Children, cloneElement, forwardRef, isValidElement } from "react";
import { useReducedMotion } from "motion/react";
import * as m from "motion/react-m";
import { motionTokens } from "./tokens";
import { useLandingViewport } from "./useLandingMobile";

export const StaggerItem = forwardRef(function StaggerItem(
  {
    as = "div",
    children,
    delay = 0,
    distance = motionTokens.distance.standard,
    duration = motionTokens.duration.reveal,
    ...props
  },
  ref,
) {
  const shouldReduceMotion = useReducedMotion();
  const StaticElement = as;

  if (shouldReduceMotion) {
    return <StaticElement ref={ref} {...props}>{children}</StaticElement>;
  }

  const MotionElement = m[as] || m.div;

  return (
    <MotionElement
      ref={ref}
      variants={{
        hidden: { opacity: 0, y: distance },
        visible: {
          opacity: 1,
          y: 0,
          transition: {
            delay,
            duration,
            ease: motionTokens.easing.entrance,
          },
        },
      }}
      {...props}
    >
      {children}
    </MotionElement>
  );
});

export const Stagger = forwardRef(function Stagger(
  {
    as = "div",
    amount,
    children,
    delay = 0,
    margin,
    stagger = motionTokens.stagger.standard,
    ...props
  },
  ref,
) {
  const shouldReduceMotion = useReducedMotion();
  const defaultViewport = useLandingViewport();
  const resolvedAmount = amount ?? defaultViewport.amount;
  const resolvedMargin = margin ?? defaultViewport.margin;
  const StaticElement = as;
  const sequencedChildren = Children.map(children, (child, index) => {
    if (!isValidElement(child) || child.type !== StaggerItem) return child;

    const start = child.props.delay ?? delay + (index * stagger);
    return cloneElement(child, {
      ...(child.props.delay == null ? { delay: start } : {}),
      ...(child.props["data-motion-start"] == null ? { "data-motion-start": start } : {}),
    });
  });

  if (shouldReduceMotion) {
    return <StaticElement ref={ref} {...props}>{sequencedChildren}</StaticElement>;
  }

  const MotionElement = m[as] || m.div;

  return (
    <MotionElement
      ref={ref}
      initial="hidden"
      whileInView="visible"
      viewport={{ amount: resolvedAmount, margin: resolvedMargin, once: true }}
      variants={{
        hidden: {},
        visible: {
          transition: {
            delayChildren: 0,
            staggerChildren: 0,
          },
        },
      }}
      {...props}
    >
      {sequencedChildren}
    </MotionElement>
  );
});

Stagger.Item = StaggerItem;
